<script setup>
// The admin side of bookings on /features/bookings: a set of booking rules as
// the Bookings page lists it (the real AreaGroupRow) and its Who can book and
// prices table (the real WhoCanBookTable), on demo data from utils/bookingDemo.
// Edit a cell and the Unsaved changes foot appears, exactly as the rules page
// does; Save and Cancel only reset the demo.
import {ref} from 'vue';
import AreaGroupRow from '~/components/admin/bookings/AreaGroupRow.vue';
import WhoCanBookTable from '~/components/admin/bookings/WhoCanBookTable.vue';
import {BOOKING_DEMO_CLUB, bookingDemoMembershipTypes, bookingDemoRulesGroup} from '~/utils/bookingDemo';

const {setAlertMessage} = useAlertService();

const dirty = ref(false);
const tableKey = ref(0);
const reset = () => { tableKey.value += 1; dirty.value = false; };
const save = () => { setAlertMessage('Saved. On the real page, players book by these rules from the next booking.'); reset(); };
const typeStatuses = Object.fromEntries(bookingDemoMembershipTypes.map((t) => [t.id, 'Live']));
</script>

<template>
  <div class="brd">
    <div class="tl-card brd-rows brd-inert">
      <area-group-row :group="bookingDemoRulesGroup" :club-id="BOOKING_DEMO_CLUB.id"/>
    </div>
    <div class="tl-card brd-table">
      <div class="brd-head">
        <span class="brd-title">Who can book and prices</span>
        <span class="brd-sub">Courts · 1 hour slots · cancel up to 1 hour before</span>
      </div>
      <div class="brd-scroll">
        <who-can-book-table :key="tableKey" :group="bookingDemoRulesGroup" :membership-types="bookingDemoMembershipTypes" :type-statuses="typeStatuses"
                          currency="gbp" payments-ready bookings-on @update:dirty="dirty = $event"/>
      </div>
      <div class="brd-swipe"><v-icon size="14" icon="mdi-gesture-swipe-horizontal"/>Swipe sideways for every column: how long, how far ahead, how many at once and the price.</div>
      <div v-if="dirty" class="brd-foot">
        <span class="tl-chip tl-chip--warn">Unsaved changes</span>
        <v-btn variant="outlined" rounded="pill" size="small" @click="reset">Cancel</v-btn>
        <v-btn color="primary" rounded="pill" size="small" @click="save">Save</v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.brd {
  display: grid;
  gap: 14px;
}

.brd-rows {
  padding: 0;
  overflow: hidden;
}

/* the row links into the product; on the demo it is to look at */
.brd-inert :deep(a),
.brd-inert :deep(button),
.brd-inert :deep([role="link"]) {
  pointer-events: none;
}

.brd-table {
  padding: 14px 16px 16px;
}

.brd-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
  margin-bottom: 10px;
}

.brd-title {
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.2px;
}

.brd-sub {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}

/* a phone sees two columns: the table scrolls inside, with a fade on the cut edge and a line saying so */
.brd-scroll {
  position: relative;
}

.brd-swipe {
  display: none;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}

@media (max-width: 599.98px) {
  .brd-scroll::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 36px;
    pointer-events: none;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0), rgb(var(--v-theme-surface)));
  }

  .brd-swipe {
    display: flex;
  }
}

.brd-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
}
</style>
